import { t } from '@lingui/core/macro';
import { Check, RotateCcw } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { CaptureActionRail, CaptureRailSecondaryButton, CaptureShutterButton } from './CaptureActionRail';

interface PhotoPreviewProps {
  photo: Blob;
  onConfirm: () => void;
  onRetake: () => void;
}

/**
 * The in-page camera's photo, before it is uploaded. The native camera app has
 * its own retake/OK screen, so only the viewfinder flow stops here. Confirm
 * hands the photo to PhotoProgress; Retake opens the viewfinder over it.
 */
export function PhotoPreview({ photo, onConfirm, onRetake }: PhotoPreviewProps) {
  const photoUrl = useMemo(() => URL.createObjectURL(photo), [photo]);

  useEffect(() => {
    return () => URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  return (
    <div className="capture-overlay bg-black">
      <div className="capture-stage absolute inset-0 right-[100px] overflow-hidden flex items-center justify-center">
        <img src={photoUrl} alt={t`Captured photo`} className="capture-media-contain" />
      </div>

      <CaptureActionRail className="absolute top-0 right-0 bottom-0">
        <CaptureShutterButton tone="green" onClick={onConfirm} aria-label={t`Confirm photo`}>
          <Check className="w-8 h-8 text-white" />
        </CaptureShutterButton>

        <CaptureRailSecondaryButton onClick={onRetake} aria-label={t`Retake photo`}>
          <RotateCcw className="w-6 h-6 text-white" />
        </CaptureRailSecondaryButton>
      </CaptureActionRail>
    </div>
  );
}
